/**
 * ðŸ”€ VersionPanel â€” Excelência Module
 *
 * Painel modal que exibe o histórico de versões
 * de um blueprint selecionado e permite criar novas versões.
 *
 * Carrega snapshots reais de process_blueprint_versions via serviço.
 * Também exibe cadeia parent → child como fallback visual.
 */

import React, { useMemo, useState, useEffect } from 'react';
import {
  X,
  GitBranch,
  Clock,
  Loader2,
  Plus,
  CheckCircle2,
  FileEdit,
  Database,
} from 'lucide-react';
import type { ProcessBlueprint, ProcessBlueprintVersion } from '../../../types/processBlueprint';

interface VersionPanelProps {
  /** Blueprint selecionado cujas versões serão exibidas */
  selectedBlueprint: ProcessBlueprint | null;
  /** Todos os blueprints da organização (para achar a cadeia) */
  allBlueprints: ProcessBlueprint[];
  /** Handler para criar nova versão */
  onCreateVersion: (blueprint: ProcessBlueprint) => void;
  /** Handler para fechar o painel */
  onClose: () => void;
  /** Indicador de operação em andamento */
  isProcessing: boolean;
  /** Função para carregar versões do DB */
  loadVersions?: (blueprintId: string) => Promise<ProcessBlueprintVersion[]>;
}

/**
 * Reconstrói a cadeia de versões de um blueprint.
 *
 * Percorre para cima (parentBlueprintId) para achar a raiz,
 * depois coleta todos os filhos que apontam para a raiz
 * ou para qualquer membro da cadeia.
 */
function buildVersionChain(
  target: ProcessBlueprint,
  all: ProcessBlueprint[],
): ProcessBlueprint[] {
  // Mapa id → blueprint
  const byId = new Map(all.map(b => [b.id, b]));

  // Achar raiz (sem parent ou parent não encontrado)
  let root = target;
  const visited = new Set<string>();
  while (root.parentBlueprintId && byId.has(root.parentBlueprintId) && !visited.has(root.id)) {
    visited.add(root.id);
    root = byId.get(root.parentBlueprintId)!;
  }

  // Coletar toda a cadeia a partir da raiz
  const chain: ProcessBlueprint[] = [root];
  const childrenOf = new Map<string, ProcessBlueprint[]>();
  for (const b of all) {
    if (b.parentBlueprintId) {
      const siblings = childrenOf.get(b.parentBlueprintId) ?? [];
      siblings.push(b);
      childrenOf.set(b.parentBlueprintId, siblings);
    }
  }

  // BFS para pegar todas as versões na ordem
  const queue = [root.id];
  const seen = new Set<string>([root.id]);
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const children = childrenOf.get(currentId) ?? [];
    children
      .sort(compareBlueprintSemver)
      .forEach(child => {
        if (!seen.has(child.id)) {
          seen.add(child.id);
          chain.push(child);
          queue.push(child.id);
        }
      });
  }

  return chain;
}

function compareBlueprintSemver(a: ProcessBlueprint, b: ProcessBlueprint): number {
  const aMajor = a.versionMajor ?? 1;
  const bMajor = b.versionMajor ?? 1;
  if (aMajor !== bMajor) return aMajor - bMajor;

  const aMinor = a.versionMinor ?? 0;
  const bMinor = b.versionMinor ?? 0;
  if (aMinor !== bMinor) return aMinor - bMinor;

  const aPatch = a.versionPatch ?? 0;
  const bPatch = b.versionPatch ?? 0;
  return aPatch - bPatch;
}

function ensureSemverLabel(semver?: string | null): string {
  return semver && semver.trim().length > 0 ? semver : '1.0.0';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const VersionPanel: React.FC<VersionPanelProps> = React.memo(
  ({ selectedBlueprint, allBlueprints, onCreateVersion, onClose, isProcessing, loadVersions }) => {
    const [snapshots, setSnapshots] = useState<ProcessBlueprintVersion[]>([]);
    const [loadingSnapshots, setLoadingSnapshots] = useState(false);

    // Load version snapshots from DB when blueprint changes
    useEffect(() => {
      if (!selectedBlueprint || !loadVersions) {
        setSnapshots([]);
        return;
      }
      setLoadingSnapshots(true);
      loadVersions(selectedBlueprint.id)
        .then(setSnapshots)
        .catch(() => setSnapshots([]))
        .finally(() => setLoadingSnapshots(false));
    }, [selectedBlueprint, loadVersions]);

    const chain = useMemo(
      () =>
        selectedBlueprint
          ? buildVersionChain(selectedBlueprint, allBlueprints)
          : [],
      [selectedBlueprint, allBlueprints],
    );

    if (!selectedBlueprint) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="w-full max-w-lg bg-white dark:bg-[var(--bg-surface)] rounded shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <GitBranch size={18} className="text-purple-500" />
              <h2 className="text-sm font-bold text-gray-900 dark:text-slate-100">
                Histórico de Versões
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>

          {/* Blueprint info */}
          <div className="px-5 py-3 bg-gray-50 dark:bg-slate-950 border-b border-gray-200 dark:border-slate-800">
            <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
              {selectedBlueprint.name}
            </p>
            <p className="text-[11px] text-gray-500 dark:text-slate-500">
              Versão atual: v{ensureSemverLabel(selectedBlueprint.versionSemver)} Â·{' '}
              {selectedBlueprint.stages.length} etapas
              {snapshots.length > 0 && ` Â· ${snapshots.length} snapshot${snapshots.length !== 1 ? 's' : ''} gravado${snapshots.length !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Version snapshots from DB */}
          {loadingSnapshots && (
            <div className="flex items-center justify-center py-4">
              <Loader2 size={16} className="animate-spin text-purple-500" />
              <span className="text-xs text-gray-500 ml-2">Carregando snapshots...</span>
            </div>
          )}

          {snapshots.length > 0 && !loadingSnapshots && (
            <div className="px-5 py-3 border-b border-gray-200 dark:border-slate-800">
              <div className="flex items-center gap-1.5 mb-2">
                <Database size={12} className="text-purple-500" />
                <span className="text-[11px] font-semibold text-gray-700 dark:text-slate-300">
                  Snapshots Persistidos
                </span>
              </div>
              <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                {snapshots.map(snap => (
                  <div
                    key={snap.id}
                    className="flex items-center justify-between p-2 rounded bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/30"
                  >
                    <div>
                      <span className="text-xs font-mono font-bold text-purple-700 dark:text-purple-300">
                        v{ensureSemverLabel(snap.versionSemver)}
                      </span>
                      {snap.changeSummary && (
                        <span className="text-[10px] text-gray-500 dark:text-slate-400 ml-2">
                          {snap.changeSummary}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-gray-400 dark:text-slate-500 flex items-center gap-1">
                      <Clock size={9} /> {formatDate(snap.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Version chain (from parent→child relationships) */}
          <div className="max-h-[260px] overflow-y-auto px-5 py-4 space-y-2">
            {chain.length === 0 && snapshots.length === 0 && !loadingSnapshots && (
              <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-6">
                Nenhuma versão encontrada.
              </p>
            )}
            {chain.length > 0 && (
              <div className="flex items-center gap-1.5 mb-2">
                <GitBranch size={12} className="text-gray-400" />
                <span className="text-[11px] font-semibold text-gray-700 dark:text-slate-300">
                  Cadeia de Versões
                </span>
              </div>
            )}
            {chain.map((bp, idx) => {
              const isCurrent = bp.id === selectedBlueprint.id;
              return (
                <div
                  key={bp.id}
                  className={`flex items-start gap-3 p-3 rounded border transition-colors ${
                    isCurrent
                      ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-slate-800 bg-white dark:bg-[var(--bg-surface)] hover:bg-gray-50 dark:hover:bg-slate-950'
                  }`}
                >
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center pt-1">
                    <div
                      className={`w-3 h-3 rounded-full border-2 ${
                        isCurrent
                          ? 'border-purple-500 bg-purple-500'
                          : bp.status === 'published'
                          ? 'border-emerald-400 bg-emerald-400'
                          : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-[var(--bg-surface)]'
                      }`}
                    />
                    {idx < chain.length - 1 && (
                      <div className="w-0.5 h-6 bg-gray-200 dark:bg-slate-700 mt-1" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-900 dark:text-slate-100 font-mono">
                        v{ensureSemverLabel(bp.versionSemver)}
                      </span>
                      {bp.status === 'published' ? (
                        <CheckCircle2 size={11} className="text-emerald-500" />
                      ) : (
                        <FileEdit size={11} className="text-amber-500" />
                      )}
                      {isCurrent && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-200 font-medium">
                          Atual
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-slate-500 mt-0.5 truncate">
                      {bp.name}
                    </p>
                    <p className="text-[10px] text-gray-400 dark:text-slate-600 flex items-center gap-1 mt-0.5">
                      <Clock size={9} /> {formatDate(bp.updatedAt)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-800 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
            >
              Fechar
            </button>
            <button
              disabled={isProcessing}
              onClick={() => onCreateVersion(selectedBlueprint)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-purple-600 hover:bg-purple-700 rounded transition-colors disabled:opacity-50"
            >
              {isProcessing ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Plus size={12} />
              )}
              Nova Versão
            </button>
          </div>
        </div>
      </div>
    );
  },
);

VersionPanel.displayName = 'VersionPanel';
